import { useEffect, useRef } from "react";
import { apiDirectCall } from "customHooks/useApi";
import { TransformSchemaFileColumn } from "../../utils";
import { IImportSettingsSteps } from "../ImportSettingsStep1";
import { useGlobalDialog } from "_ReactContext/GlobalDialogContext";

export function useHandleUpload(props: IImportSettingsSteps) {
    const {
        file,
        setFullData,
        setParsedFile,
        transformSchema,
        setTransformSchema,
        setActiveStep,
        tBulk,
        setFile,
        importApplicantsLimit
    } = props;
    const { callDialog } = useGlobalDialog();
    const generation = useRef(0);
    const latest = useRef({ file, transformSchema });
    latest.current = { file, transformSchema };

    useEffect(() => {
        return () => {
            generation.current += 1;
        };
    }, [file, transformSchema?.id]);

    // Step 2 reads file data only; Step 3 may initialize a still-empty template.
    const handleUpload = async (initializeColumns = true) => {
        if (!file) return;
        const request = ++generation.current;
        setParsedFile?.(null);
        const isCurrent = () =>
            request === generation.current &&
            latest.current.file === file &&
            latest.current.transformSchema?.id === transformSchema?.id;
        const formData = new FormData();
        formData.append("file", file);
        try {
            const response = await apiDirectCall(
                "/exportimportdata/exceltojson",
                "post",
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            if (!isCurrent()) return null;
            const result = response.data?.data || {};
            const headers = result.headers;
            const fieldsData = result.data;
            if (
                !Array.isArray(headers) ||
                !headers.length ||
                !Array.isArray(fieldsData)
            )
                return null;
            if (
                !fieldsData.length ||
                fieldsData.length > importApplicantsLimit
            ) {
                callDialog({
                    title: tBulk("COMPLEOGENERAL_ERROR"),
                    bodyText:
                        fieldsData.length > importApplicantsLimit
                            ? tBulk("importLimitMessage", {
                                  limit: importApplicantsLimit
                              })
                            : tBulk("errorEmptyFile"),
                    agreeButtonText: tBulk("COMPLEOGENERAL_CLOSE"),
                    agreeFunction: async () => {
                        if (!isCurrent()) return;
                        setActiveStep(0);
                        setFile(null);
                    }
                });
                return null;
            }
            // The server returns the raw header row: numeric cells stay numbers and
            // blank cells arrive as null. String() matches the row object keys.
            const cols: TransformSchemaFileColumn[] = headers.map((header) => ({
                id: String(header),
                columnName: String(header)
            }));
            if (initializeColumns && transformSchema?.id) {
                setTransformSchema((current) => {
                    // Never project a reusable template onto this file's headers,
                    // or replace an edit made while the file was being parsed.
                    if (
                        !isCurrent() ||
                        !current ||
                        current !== transformSchema ||
                        current.map?.length
                    ) {
                        return current;
                    }
                    return { ...current, map: cols };
                });
            }
            setFullData(fieldsData);
            setParsedFile?.(file);
            return cols;
        } catch (error) {
            if (isCurrent()) console.error("Error uploading file:", error);
        }
    };
    return { handleUpload };
}
